import React, { useContext, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import EditIcon from '@mui/icons-material/Edit';

import { useAppDispatch } from '../../../../app/hooks';
import { selectProject } from '../../../../features/project/projectSlice';
import DealOptions from '../../DealOptions';
import { UnitContext } from '../../../../context/UnitContext';
import { numToCurrency } from '../../../../utils/Functions';
import { Flex, FlexEnd } from '../../../../commonStyles';
import { selectUser } from '../../../../features/auth/authSlice';
import { useCreateActivity } from '../../../../features/activity/activityHooks';
import { showSuccessSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { DealContext } from '../../../../context/DealContext';
import PendingOptions from './PendingOptions';
import { IDealOption } from '../../../../types/CreateDealForm';
import { IUnitHistory } from '../../../../types/unit';

const OptionInfo = (props: any) => {
  const { unit, filteredDeal, setFilteredDeal } = useContext(UnitContext);
  const { document, setDocument, pendingOptions, setPendingOptions } = useContext(DealContext);

  const storeDispatch = useAppDispatch();

  const user = useSelector(selectUser);
  const createActivity = useCreateActivity();
  const project = useSelector(selectProject);
  const [errors, setErrors] = useState<any>(null);
  const [editOptions, setEditOptions] = useState<boolean>(false);
  const [options, setOptions] = useState<IDealOption[]>([]);

  // Queries and Mutations

  const [updateUnit] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {},
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [saveOptions] = useMutation(SAVEWAITLIST, {
    onCompleted: (data) => {
      createActivity({
        project: project._id,
        user: user._id,
        deal: filteredDeal._id,
        title: `Option`,
        content: `Options has been manually updated. ${data.dealUpdateById.record.options
          .map((option: IDealOption) => `${option.name}: x${option.purchaseAmount} - ${numToCurrency.format(option.amount!)}`)
          .join(', ')}`,
      });
      setFilteredDeal({
        ...filteredDeal,
        options: data.dealUpdateById.record.options,
      });
      let history = {
        type: 'Options Update',
        description: `Options has been manually updated. ${data.dealUpdateById.record.options
          .map((option: IDealOption) => `${option.name}: x${option.purchaseAmount} - ${numToCurrency.format(option.amount!)}`)
          .join(', ')}`,
        timestamp: new Date(),
        user: user._id,
      };
      updateUnit({
        variables: {
          _id: unit._id,
          record: {
            history: [
              ...unit.history.map((history: IUnitHistory) => {
                return {
                  ...history,
                  user: history.user ? history.user._id : null,
                };
              }),
              history,
            ],
          },
        },
      });
      setOptions([]);
      setEditOptions(false);
      storeDispatch(showSuccessSnackbar(`Options has been updated`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  // Functions

  const totalOptionsPrice = (type: string) => {
    let totalOptions = unit.basePrice;
    if (type === 'options') {
      totalOptions = 0;
    }

    if (type === 'total') {
      if (filteredDeal.pendingOptions.length > 0) {
        totalOptions = filteredDeal.pendingOptions.reduce((a: any, b: any) => {
          return a + b.amount;
        }, unit.basePrice);
      } else if (pendingOptions.length > 0) {
        totalOptions = pendingOptions.reduce((a: any, b: any) => {
          return a + b.amount;
        }, unit.basePrice);
      }
      return totalOptions;
    } else if (type === 'options') {
      if (filteredDeal.pendingOptions.length > 0) {
        totalOptions = filteredDeal.pendingOptions.reduce((a: any, b: any) => {
          return a + b.amount;
        }, 0);
      } else if (pendingOptions.length > 0) {
        totalOptions = pendingOptions.reduce((a: any, b: any) => {
          return a + b.amount;
        }, 0);
      }
      return totalOptions;
    }
  };

  const handleOptions = (type: string = '') => {
    setEditOptions(false);
    setOptions([]);
    let allOptions = project.options.map((option: any) => {
      let selectedOption = filteredDeal.options.find((dealOption: IDealOption) => option.name === dealOption.name);
      return {
        name: option.name,
        purchaseAmount: selectedOption ? selectedOption.purchaseAmount : 0,
        amount: selectedOption ? selectedOption.amount : 0,
        rental: option.rental,
      };
    });
    if (type) {
      setDocument('Options');
    }
    setPendingOptions(allOptions);
  };

  const handleEditOptions = () => {
    if (!filteredDeal.options.length) {
      let allOptions = project.options.map((option: any) => {
        let selectedOption = filteredDeal.options.find((dealOption: IDealOption) => option.name === dealOption.name);
        return {
          name: option.name,
          purchaseAmount: selectedOption ? selectedOption.purchaseAmount : 0,
          amount: selectedOption ? selectedOption.amount : 0,
          rental: option.rental,
        };
      });

      setOptions(allOptions);
    }
    setEditOptions(!editOptions);
  };

  const handleSave = () => {
    let allOptions = [];
    if (options.length) {
      allOptions = options;
    } else {
      allOptions = filteredDeal.options;
    }

    allOptions = allOptions
      .filter((option: IDealOption) => option.purchaseAmount)
      .map((option: IDealOption) => {
        return {
          name: option.name,
          purchaseAmount: option.purchaseAmount,
          amount: option.amount,
        };
      });

    saveOptions({ variables: { _id: filteredDeal._id, record: { options: allOptions } } });
  };

  return (
    <div>
      <Flex sx={{ my: 2 }}>
        <Typography variant="h4">
          <strong>Current Options</strong>
        </Typography>
        {pendingOptions.length === 0 ? (
          <EditIcon sx={{ cursor: 'pointer', fontSize: 16, ml: 1 }} onClick={() => handleEditOptions()} />
        ) : null}
      </Flex>
      <DealOptions
        options={options.length ? options : filteredDeal.options}
        setOptions={options.length ? setOptions : setFilteredDeal}
        disabled={editOptions ? false : true}
      />
      {filteredDeal.pendingOptions.length > 0 ? (
        <div>
          <h3
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <em>Pending Options</em>
          </h3>
          <em>
            These options are currently pending until the amendment has been executed. After the amendment has been executed, it will
            replace the options above. If you would like to remove these options, please delete the Option/Unit Transfer Amendment in the
            amendments section
          </em>
          {filteredDeal.pendingRental ? (
            <Box sx={{ mt: 2 }}>
              Pending Rental Change: <strong>{numToCurrency.format(filteredDeal.pendingRental)}</strong>
            </Box>
          ) : null}
          <Box sx={{ mt: 2 }}>
            Total Price for Pending Options: <strong>{numToCurrency.format(totalOptionsPrice('total'))}</strong>
          </Box>
          <DealOptions options={filteredDeal.pendingOptions} disabled={true} />
        </div>
      ) : null}
      {pendingOptions.length > 0 && document === 'Options' ? (
        <PendingOptions errors={errors} setErrors={setErrors} totalOptionsPrice={totalOptionsPrice} />
      ) : filteredDeal.pendingOptions.length === 0 && !document ? (
        <FlexEnd sx={{ mt: 2 }}>
          {editOptions ? (
            <Button onClick={() => handleSave()} name={'update'} color="success" variant="contained" sx={{ mr: 2 }}>
              Save Options
            </Button>
          ) : null}
          <Button
            onClick={() => {
              handleOptions('Options');
            }}
            name={'update'}
            color="primary"
            variant="contained"
          >
            Create Option Amendment
          </Button>
        </FlexEnd>
      ) : null}
    </div>
  );
};

const SAVEWAITLIST = gql`
  mutation dealUpdateById($_id: MongoID!, $record: UpdateByIdDealInput!) {
    dealUpdateById(_id: $_id, record: $record) {
      record {
        options {
          name
          purchaseAmount
          amount
        }
      }
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateById($_id: MongoID!, $record: UpdateByIdUnitInput!) {
    unitUpdateById(_id: $_id, record: $record) {
      record {
        _id
      }
    }
  }
`;

export default OptionInfo;
